import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import { 
  DocumentTextIcon, 
  CloudArrowUpIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

const BulkQuestionUpload = ({ onComplete }) => {
  const [uploadMethod, setUploadMethod] = useState('pdf'); // 'pdf' or 'csv'
  const [file, setFile] = useState(null);
  const [answerFile, setAnswerFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [metadata, setMetadata] = useState({
    year: '',
    subject: '',
    section: 'A',
    topic: ''
  });
  const [questions, setQuestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = async () => {
    if (!file) {
      setError('Please select a PDF or DOCX file');
      return;
    }

    setParsing(true);
    setError('');
    
    const formData = new FormData();
    formData.append('attachment', file);

    try {
      const res = await api.post('/questions/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setQuestions(res.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse questions from file');
      setQuestions([]);
    } finally {
      setParsing(false);
    }
  };

  const handleAnswerFileUpload = async () => {
    if (!answerFile) {
      setError('Please select an answer file');
      return;
    }

    setParsing(true);
    setError('');
    
    const formData = new FormData();
    formData.append('attachment', answerFile);

    try {
      const res = await api.post('/questions/parse-answers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const answersData = res.data; // Array of { questionNumber, answer }
      
      if (!Array.isArray(answersData)) {
        setError('Invalid answer file format. Expected array of answers.');
        return;
      }
      
      // Create a map of question number to answer
      const answerMap = {};
      answersData.forEach(item => {
        if (item.questionNumber && item.answer) {
          answerMap[item.questionNumber] = item.answer;
        }
      });
      
      console.log('📝 Answer map:', answerMap);
      
      // Match answers by question number
      const updatedQuestions = questions.map((q, index) => {
        const questionNum = index + 1; // Questions are 1-indexed
        const answerLetter = answerMap[questionNum];
        
        if (!answerLetter) {
          return q; // No answer for this question
        }
        
        return {
          ...q,
          answer: answerLetter
        };
      });
      
      setQuestions(updatedQuestions);
      
      const matchedCount = Object.keys(answerMap).length;
      setError(`✅ Successfully matched ${matchedCount} answers to their specific question numbers! Check the answer fields below.`);
      setTimeout(() => setError(''), 3000); // Clear success message after 3 seconds
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse answers from file');
    } finally {
      setParsing(false);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = `question,optionA,optionB,optionC,optionD,optionE,correctOption,explanation
"What is the capital of France?","London","Berlin","Paris","Madrid","Rome","C","Paris is the capital and largest city of France"
"Which planet is closest to the Sun?","Venus","Mercury","Earth","Mars","Jupiter","B","Mercury is the closest planet to the Sun in our solar system"
"What is 5 + 3?","6","7","8","9","10","C","Basic addition: 5 + 3 = 8"
"What is the largest continent?","Asia","Africa","Europe","North America","South America","A","Asia is the largest continent by both area and population"`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    if (!metadata.year || !metadata.subject) {
      setError('Year and Subject are required for CSV upload');
      return;
    }

    setParsing(true);
    setError('');
    
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('year', metadata.year);
    formData.append('subject', metadata.subject);
    formData.append('section', metadata.section);
    if (metadata.topic) formData.append('topic', metadata.topic);

    try {
      const res = await api.post('/questions/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess(true);
      setTimeout(() => {
        if (onComplete) onComplete(res.data);
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import questions from CSV');
    } finally {
      setParsing(false);
    }
  };

  const handleBulkSave = async () => {
    if (questions.length === 0) {
      setError('No questions to save');
      return;
    }

    if (!metadata.year || !metadata.subject) {
      setError('Year and Subject are required');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await api.post('/questions/bulk', {
        questions: questions,
        metadata: {
          year: parseInt(metadata.year, 10),
          subject: metadata.subject,
          section: metadata.section,
          topic: metadata.topic || null
        }
      });

      setSuccess(true);
      setTimeout(() => {
        if (onComplete) onComplete(response.data);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save questions to bank');
    } finally {
      setUploading(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
        <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-green-800 mb-2">Questions Saved Successfully!</h3>
        <p className="text-green-700">{questions.length} questions have been added to the question bank.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Upload Method Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Choose Upload Method
        </h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setUploadMethod('pdf')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              uploadMethod === 'pdf' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📄 PDF/DOCX Files
          </button>
          <button
            onClick={() => setUploadMethod('csv')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              uploadMethod === 'csv' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📊 CSV File (Recommended)
          </button>
        </div>
      </div>

      {uploadMethod === 'csv' ? (
        /* CSV Upload Section */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CloudArrowUpIcon className="w-5 h-5 text-blue-600 mr-2" />
            CSV Upload (Questions + Answers)
          </h2>
          
          <div className="space-y-4">
            {/* CSV Format Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-blue-900">📋 CSV Format Required:</h3>
                <button
                  onClick={downloadCSVTemplate}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
                >
                  📥 Download Template
                </button>
              </div>
              <p className="text-sm text-blue-800 mb-2">Your CSV file should have these columns:</p>
              <code className="text-xs bg-blue-100 p-2 rounded block">
                question,optionA,optionB,optionC,optionD,optionE,correctOption,explanation
              </code>
              <p className="text-xs text-blue-700 mt-2">
                • <strong>question</strong>: The question text<br/>
                • <strong>optionA-E</strong>: Multiple choice options (optionE is optional)<br/>
                • <strong>correctOption</strong>: A, B, C, D, or E<br/>
                • <strong>explanation</strong>: Optional explanation
              </p>
            </div>

            {/* Metadata for CSV */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="csv-year" className="block text-sm font-medium text-gray-700 mb-2">
                  Year <span className="text-red-500">*</span>
                </label>
                <input
                  id="csv-year"
                  type="number"
                  value={metadata.year}
                  onChange={e => setMetadata({...metadata, year: e.target.value})}
                  placeholder="e.g., 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="csv-subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  id="csv-subject"
                  type="text"
                  value={metadata.subject}
                  onChange={e => setMetadata({...metadata, subject: e.target.value})}
                  placeholder="e.g., Mathematics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <div className="space-y-2">
                <label className="cursor-pointer bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                  Choose CSV File
                  <input
                    type="file"
                    accept=".csv"
                    onChange={e => setCsvFile(e.target.files[0])}
                    className="sr-only"
                  />
                </label>
                <p className="text-sm text-gray-500">CSV files with questions and answers</p>
                {csvFile && (
                  <p className="text-sm text-green-600 font-medium">✓ {csvFile.name}</p>
                )}
              </div>
            </div>
            
            <button
              onClick={handleCsvUpload}
              disabled={!csvFile || !metadata.year || !metadata.subject || parsing}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {parsing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importing Questions...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Import Questions & Answers
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* PDF/DOCX Upload Section */
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CloudArrowUpIcon className="w-5 h-5 text-blue-600 mr-2" />
              1. Upload Question File
            </h2>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="space-y-2">
                  <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    Choose Questions File
                    <input
                      type="file"
                      accept=".pdf,.docx,.doc"
                      onChange={e => setFile(e.target.files[0])}
                      className="sr-only"
                    />
                  </label>
                  <p className="text-sm text-gray-500">PDF, DOC, or DOCX files up to 10MB</p>
                  {file && (
                    <p className="text-sm text-green-600 font-medium">✓ {file.name}</p>
                  )}
                </div>
              </div>
              
              <button
                onClick={handleFileUpload}
                disabled={!file || parsing}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                {parsing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Parsing Questions...
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                    Parse Questions
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Answer File Upload */}
          {questions.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                2. Upload Answer Key (Optional)
              </h2>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-amber-800 mb-2">📝 Supported Answer Formats:</h4>
                <div className="text-sm text-amber-700 space-y-1">
                  <div>• <code>1. D. representative fraction</code></div>
                  <div>• <code>2. B. trigonometrical station</code></div>
                  <div>• <code>3) A</code> or <code>3. A</code></div>
                  <div>• <code>4. E</code></div>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  The system will automatically extract the letter (A, B, C, D, E) from your answer file.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <div className="space-y-2">
                    <label className="cursor-pointer bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                      Choose Answer File
                      <input
                        type="file"
                        accept=".pdf,.docx,.doc"
                        onChange={e => setAnswerFile(e.target.files[0])}
                        className="sr-only"
                      />
                    </label>
                    <p className="text-sm text-gray-500">Answer key file matching your questions</p>
                    {answerFile && (
                      <p className="text-sm text-green-600 font-medium">✓ {answerFile.name}</p>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={handleAnswerFileUpload}
                  disabled={!answerFile || parsing}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  {parsing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Parsing Answers...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5 mr-2" />
                      Parse Answers
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Metadata Section */}
      {questions.length > 0 && uploadMethod === 'pdf' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            3. Set Question Metadata
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="bulk-year" className="block text-sm font-medium text-gray-700 mb-2">
                Year <span className="text-red-500">*</span>
              </label>
              <input
                id="bulk-year"
                type="number"
                value={metadata.year}
                onChange={e => setMetadata({...metadata, year: e.target.value})}
                placeholder="e.g., 2024"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label htmlFor="bulk-subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                id="bulk-subject"
                type="text"
                value={metadata.subject}
                onChange={e => setMetadata({...metadata, subject: e.target.value})}
                placeholder="e.g., Mathematics"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label htmlFor="bulk-section" className="block text-sm font-medium text-gray-700 mb-2">Section</label>
              <select
                id="bulk-section"
                value={metadata.section}
                onChange={e => setMetadata({...metadata, section: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="A">Section A</option>
                <option value="B">Section B</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="bulk-topic" className="block text-sm font-medium text-gray-700 mb-2">Topic (Optional)</label>
              <input
                id="bulk-topic"
                type="text"
                value={metadata.topic}
                onChange={e => setMetadata({...metadata, topic: e.target.value})}
                placeholder="e.g., Algebra"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Questions Review Section */}
      {questions.length > 0 && uploadMethod === 'pdf' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                4. Review Questions ({questions.length})
              </h2>
              {questions.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {questions.filter(q => q.answer).length} of {questions.length} questions have answers
                  {questions.filter(q => q.answer).length === questions.length && (
                    <span className="text-green-600 ml-2">✓ All answered</span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={handleBulkSave}
              disabled={uploading || !metadata.year || !metadata.subject}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Save All to Bank
                </>
              )}
            </button>
          </div>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {questions.map((question, index) => (
              <div 
                key={index} 
                className={`border rounded-lg p-4 ${
                  question.parseError 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-200'
                }`}
              >
                {question.parseError && (
                  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-3 rounded text-sm">
                    <div className="flex items-start">
                      <span className="text-xl mr-2">⚠️</span>
                      <div>
                        <p className="font-bold">Parsing Error</p>
                        <p className="text-xs mt-1">
                          This question failed to parse. Please manually enter the question text and options below.
                        </p>
                        {question.errorMessage && (
                          <p className="text-xs mt-1 font-mono bg-red-200 p-1 rounded">Error: {question.errorMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between mb-2">
                  <h4 className={`font-medium ${question.parseError ? 'text-red-900' : 'text-gray-900'}`}>
                    Question {index + 1} {question.parseError && <span className="text-red-600 text-sm">(⚠️ Needs Manual Input)</span>}
                  </h4>
                  <button
                    onClick={() => removeQuestion(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
                
                <textarea
                  value={question.text || ''}
                  onChange={e => updateQuestion(index, 'text', e.target.value)}
                  rows={2}
                  placeholder="Enter the question text here..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 text-sm ${
                    question.parseError
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-white placeholder-red-400'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                />
                
                {/* Answer input */}
                <div className="mt-2">
                  <label htmlFor={`answer-${index}`} className="block text-xs text-gray-500 mb-1">
                    Answer: {question.answer && !question.parseError && (
                      <span className="text-green-600 font-medium">✓ Auto-matched</span>
                    )}
                  </label>
                  <input
                    id={`answer-${index}`}
                    value={question.answer || ''}
                    onChange={e => updateQuestion(index, 'answer', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                      question.parseError
                        ? 'border-red-300 bg-red-50'
                        : question.answer 
                          ? 'border-green-300 bg-green-50' 
                          : 'border-gray-300'
                    }`}
                    placeholder="Enter answer (A, B, C, D, E, or text)"
                  />
                </div>
                
                {question.options && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Options:</p>
                    <div className="text-sm text-gray-700">
                      {question.options.map((option, optIndex) => (
                        <div key={optIndex}>• {option}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

BulkQuestionUpload.propTypes = {
  onComplete: PropTypes.func
};

export default BulkQuestionUpload;
